<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import AppLayout from '@/layouts/AppLayout.vue';
import { type BreadcrumbItem } from '@/types';
import { apiGet } from '@/utils/api';
import { Head, Link } from '@inertiajs/vue3';
import { Bot, Plus, Search } from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';

interface Agent {
  id: number;
  name: string;
  description?: string;
  avatar_url?: string;
  color?: string;
  is_active: boolean;
  configuration: {
    model: string;
    systemMessage: string;
    temperature: number;
  };
  created_at: string;
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: 'Dashboard',
    href: '/dashboard',
  },
  {
    title: 'Agents',
    href: '/agents',
  },
];

const agents = ref<Agent[]>([]);
const loading = ref(true);
const searchQuery = ref('');

const fetchAgents = async () => {
  try {
    const response = await apiGet('/api/agents');
    agents.value = await response.json();
  } catch (error) {
    console.error('Failed to fetch agents:', error);
  } finally {
    loading.value = false;
  }
};

const filteredAgents = computed(() => {
  if (!searchQuery.value) return agents.value;

  return agents.value.filter(
    (agent) =>
      agent.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      agent.description?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      agent.configuration.model.toLowerCase().includes(searchQuery.value.toLowerCase()),
  );
});

onMounted(() => {
  fetchAgents();
});
</script>

<template>
  <Head title="AI Agents" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-4">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold">AI Agents</h1>
          <p class="text-muted-foreground">Manage your AI assistants</p>
        </div>
        <Button as-child>
          <Link :href="route('agents.create.page')" class="inline-flex items-center gap-2">
            <Plus class="h-4 w-4" />
            Create Agent
          </Link>
        </Button>
      </div>

      <!-- Search -->
      <div class="relative max-w-md">
        <Search class="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
        <Input v-model="searchQuery" placeholder="Search agents..." class="pl-10" />
      </div>

      <!-- Agents Grid -->
      <div v-if="loading" class="py-8 text-center">
        <div class="text-muted-foreground">Loading agents...</div>
      </div>

      <div v-else-if="filteredAgents.length === 0 && !searchQuery" class="py-12 text-center">
        <Bot class="text-muted-foreground mx-auto mb-4 h-16 w-16" />
        <h3 class="mb-2 text-xl font-semibold">No agents yet</h3>
        <p class="text-muted-foreground mb-6">Create your first AI agent to get started</p>
        <Button as-child>
          <Link :href="route('agents.create.page')" class="inline-flex items-center gap-2">
            <Plus class="h-4 w-4" />
            Create Your First Agent
          </Link>
        </Button>
      </div>

      <div v-else-if="filteredAgents.length === 0" class="py-12 text-center">
        <Search class="text-muted-foreground mx-auto mb-4 h-16 w-16" />
        <h3 class="mb-2 text-xl font-semibold">No agents found</h3>
        <p class="text-muted-foreground">Try adjusting your search terms</p>
      </div>

      <div v-else class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card v-for="agent in filteredAgents" :key="agent.id" class="transition-shadow hover:shadow-lg">
          <CardHeader>
            <div class="flex items-center gap-3">
              <div
                class="flex h-12 w-12 items-center justify-center rounded-full text-lg font-semibold text-white"
                :style="{ backgroundColor: agent.color || '#6366f1' }"
              >
                {{ agent.name.charAt(0).toUpperCase() }}
              </div>
              <div class="flex-1">
                <CardTitle class="text-lg">{{ agent.name }}</CardTitle>
                <div class="mt-1 flex items-center gap-2">
                  <span class="bg-secondary text-secondary-foreground rounded-full px-2 py-1 text-xs">
                    {{ agent.configuration.model }}
                  </span>
                  <span
                    v-if="agent.is_active"
                    class="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800 dark:bg-green-900 dark:text-green-200"
                  >
                    Active
                  </span>
                  <span v-else class="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-800 dark:bg-gray-800 dark:text-gray-200"> Inactive </span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p v-if="agent.description" class="text-muted-foreground mb-4 line-clamp-2 text-sm">
              {{ agent.description }}
            </p>
            <p v-else class="text-muted-foreground mb-4 text-sm italic">No description provided</p>

            <div class="flex items-center justify-between">
              <span class="text-muted-foreground text-xs"> Created {{ new Date(agent.created_at).toLocaleDateString() }} </span>
              <div class="flex gap-2">
                <Button variant="outline" size="sm" as-child>
                  <Link :href="route('agents.edit.page', agent.id)"> Edit </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </AppLayout>
</template>
